import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link, useSearchParams, useNavigate } from 'react-router-dom';
import PortalV2Layout from './PortalV2Layout';
import EmbeddedStripePaymentElement from '../components/EmbeddedStripePaymentElement';
import { useAuth } from '../contexts/AuthContext';
import { StatusPill } from './AdminV2Dashboard';
import JobStatusStepper from './JobStatusStepper';
import {
  Download, MapPin, FileText, Copy, Camera, Receipt, CreditCard,
  CheckCircle, AlertCircle, X, Building2, Scale, Calendar, ArrowLeft, ScrollText, ExternalLink,
  ChevronLeft, ChevronRight,
} from 'lucide-react';

const fmtUSD = (v) => `$${Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (s) => s ? new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
const fmtDateTime = (s) => s ? new Date(s).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true }) : '—';

const fmtAddress = (a) => {
  if (!a) return '—';
  const parts = [a.address1 || a.street1, a.address2, a.city && a.state ? `${a.city}, ${a.state}` : (a.city || a.state), a.postal_code || a.zip].filter(Boolean);
  return parts.join(', ');
};

// NET30 aging
const getAging = (inv) => {
  if (!inv?.issued_on) return null;
  const issued = new Date(inv.issued_on);
  const due = new Date(issued); due.setDate(due.getDate() + 30);
  const days = Math.floor((Date.now() - due.getTime()) / 86400000);
  if (days <= 0) return { days: 0, label: days === 0 ? 'Due today' : `Due in ${Math.abs(days)} days`, color: '#60a5fa' };
  if (days <= 30) return { days, label: `${days} days overdue`, color: 'var(--pv2-amber-text)' };
  if (days <= 60) return { days, label: `${days} days overdue`, color: '#fb923c' };
  return { days, label: `${days} days overdue`, color: 'var(--pv2-red-bright)' };
};

const Field = ({ label, value, mono }) => (
  <div>
    <div className="pv2-mono-sm" style={{ marginBottom: 4, color: 'var(--pv2-red-bright)' }}>{label}</div>
    <div style={{ fontSize: 14, fontFamily: mono ? 'JetBrains Mono, monospace' : 'inherit' }}>{value || '—'}</div>
  </div>
);

// Detect Apple device for native Maps app, otherwise Google Maps.
const isAppleDevice = () => {
  if (typeof navigator === 'undefined') return false;
  return /iPhone|iPad|iPod|Macintosh/.test(navigator.userAgent);
};

const buildMapHref = (addr) => {
  const line1 = [addr.address1, addr.address2].filter(Boolean).join(' ');
  const line2 = [addr.city, addr.state, addr.postal_code].filter(Boolean).join(' ');
  const q = encodeURIComponent([line1, line2].filter(Boolean).join(', '));
  return isAppleDevice() ? `https://maps.apple.com/?q=${q}` : `https://www.google.com/maps/search/?api=1&query=${q}`;
};

// Renders an address as: "<line1>, <line2>" on first line, then "<city>, <state> <zip>" on second line.
// The whole block is a clickable link that opens Apple Maps on Apple devices, Google Maps otherwise.
const ClickableAddress = ({ addr, inline = false }) => {
  if (!addr) return null;
  const line1 = [addr.address1, addr.address2].filter(Boolean).join(', ');
  const line2 = [
    addr.city,
    [addr.state, addr.postal_code].filter(Boolean).join(' '),
  ].filter(Boolean).join(', ');
  if (inline) {
    const oneLine = [line1, line2].filter(Boolean).join(', ');
    return (
      <a
        href={buildMapHref(addr)}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: 'inherit', textDecoration: 'none', fontSize: 15, fontWeight: 500 }}
        title="Open in Maps"
        data-testid="pv2-address-map-link"
      >
        {oneLine}
      </a>
    );
  }
  return (
    <a
      href={buildMapHref(addr)}
      target="_blank"
      rel="noopener noreferrer"
      style={{ display: 'block', color: 'inherit', textDecoration: 'none' }}
      title="Open in Maps"
      data-testid="pv2-address-map-link"
    >
      {line1 && <div style={{ fontSize: 15, fontWeight: 500 }}>{line1}</div>}
      {line2 && <div style={{ fontSize: 14, color: 'var(--pv2-text-muted)' }}>{line2}</div>}
    </a>
  );
};

const JobV2Detail = () => {
  const { jobId } = useParams();
  const { authFetch, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const [invoice, setInvoice] = useState(null);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [showPay, setShowPay] = useState(false);

  const [attemptPhotos, setAttemptPhotos] = useState([]);
  const [lightbox, setLightbox] = useState(null); // { images:[url], index, attempt } — full-size gallery viewer
  const [paymentSuccess, setPaymentSuccess] = useState(searchParams.get('payment') === 'success');

  useEffect(() => {
    if (paymentSuccess) {
      const np = new URLSearchParams(searchParams); np.delete('payment'); np.delete('session_id');
      setSearchParams(np, { replace: true });
      const t = setTimeout(() => setPaymentSuccess(false), 10000);
      return () => clearTimeout(t);
    }
  }, [paymentSuccess, searchParams, setSearchParams]);

  const fetchPhotos = useCallback(async () => {
    try {
      const ep = isAdmin ? `/api/admin/jobs/${jobId}/photos` : `/api/portal/jobs/${jobId}/photos`;
      const r = await authFetch(ep);
      if (r.ok) setAttemptPhotos((await r.json()).attempts || []);
    } catch {}
  }, [authFetch, isAdmin, jobId]);

  const fetchInvoice = useCallback(async (jobNum) => {
    if (!jobNum) return;
    setInvoiceLoading(true);
    try {
      const token = localStorage.getItem('westex_portal_token');
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const r = await fetch(`${API_URL}/api/portal/invoices/by-job/${jobNum}`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (r.ok) { const data = await r.json(); setInvoice(data.invoice || null); } else setInvoice(null);
    } catch { setInvoice(null); }
    finally { setInvoiceLoading(false); }
  }, []);

  const fetchJob = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const endpoint = isAdmin ? `/api/admin/jobs/${jobId}` : `/api/portal/jobs/${jobId}`;
      const r = await authFetch(endpoint);
      if (!r.ok) {
        if (r.status === 404) throw new Error('Job not found');
        if (r.status === 403) throw new Error('You do not have access to this job');
        throw new Error('Failed to fetch job details');
      }
      const data = await r.json();
      const j = data.job || data;
      setJob(j);
      fetchPhotos();
      if (!isAdmin) {
        if (j.servemanager_job_number) fetchInvoice(j.servemanager_job_number);
      }
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [authFetch, isAdmin, jobId, fetchPhotos, fetchInvoice]);

  useEffect(() => { fetchJob(); }, [fetchJob]);

  const copyJobNum = async () => {
    if (!job?.servemanager_job_number) return;
    try { await navigator.clipboard.writeText(job.servemanager_job_number); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch {}
  };

  const downloadDoc = async (doc, kind = 'document') => {
    try {
      // kind: 'document' = service docs, 'attachment' = misc attachments (affidavits/photos)
      const segment = kind === 'attachment' ? 'attachments' : 'documents';
      const endpoint = isAdmin
        ? `/api/admin/jobs/${jobId}/${segment}/${doc.id}/download`
        : `/api/portal/jobs/${jobId}/${segment}/${doc.id}/download`;
      const r = await authFetch(endpoint);
      if (r.ok) {
        const data = await r.json();
        if (data.download_url) window.open(data.download_url, '_blank');
      }
    } catch {}
  };

  const createPaymentIntent = async () => {
    if (!invoice) throw new Error('No invoice loaded');
    const r = await authFetch('/api/portal/invoices/pay-intent', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ invoice_id: invoice.id, origin_url: window.location.origin }),
    });
    if (!r.ok) throw new Error((await r.json()).detail || 'Could not start payment');
    return await r.json();
  };

  const closePay = () => {
    setShowPay(false);
    if (job?.servemanager_job_number) fetchInvoice(job.servemanager_job_number);
  };

  const [rushBusy, setRushBusy] = useState(false);
  const setRushOverride = async (val) => {
    setRushBusy(true);
    try {
      const r = await authFetch(`/api/admin/jobs/${jobId}/rush-override`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ override: val }),
      });
      if (r.ok) {
        const d = await r.json();
        setJob(p => ({ ...p, is_rush: d.is_rush, rush_override: d.rush_override }));
      }
    } finally { setRushBusy(false); }
  };

  // Lightbox gallery navigation (wrap-around) + keyboard arrows/escape.
  const lightboxCount = lightbox?.images?.length || 0;
  const showLightboxNav = lightboxCount > 1;
  const stepLightbox = useCallback((dir) => {
    setLightbox((lb) => {
      if (!lb || !lb.images?.length) return lb;
      const n = lb.images.length;
      return { ...lb, index: (lb.index + dir + n) % n };
    });
  }, []);

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setLightbox(null);
      else if (e.key === 'ArrowRight') stepLightbox(1);
      else if (e.key === 'ArrowLeft') stepLightbox(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox, stepLightbox]);

  // Find last attempt's server name
  const lastAttempt = job?.attempts?.length > 0 ? job.attempts[job.attempts.length - 1] : null;

  const balance = invoice?.portal_paid ? 0 : parseFloat(invoice?.balance_due || 0);
  const paidDisplay = invoice?.portal_paid ? (parseFloat(invoice?.total_paid || 0) || parseFloat(invoice?.total || 0)) : parseFloat(invoice?.total_paid || 0);

  // Open the ServeManager invoice PDF in a new tab. Same endpoint is used
  // pre-payment AND post-payment — ServeManager auto-applies the "PAID"
  // watermark on its end once the invoice's balance is zero, so this also
  // serves as the client's receipt after they pay through Stripe.
  const viewInvoicePdf = useCallback(async () => {
    if (!invoice?.id) return;
    try {
      const res = await authFetch(`/api/portal/invoices/${invoice.id}/pdf`);
      if (!res.ok) return;
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      // Keep the object URL alive briefly so the new tab can read it.
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (_e) {
      // Silent fail — button is best-effort.
    }
  }, [invoice, authFetch]);
  const isPaid = invoice && (invoice.portal_paid || balance <= 0);
  const aging = invoice && !isPaid ? getAging(invoice) : null;

  return (
    <PortalV2Layout
      crumbs={
        <span>
          <Link to={isAdmin ? '/portal/admin/jobs' : '/portal/jobs'} style={{ color: 'inherit' }}>Jobs</Link>
          <span style={{ margin: '0 8px', opacity: 0.5 }}>/</span>
          <strong>{job?.servemanager_job_number || jobId}</strong>
        </span>
      }
    >
      <Link to={isAdmin ? '/portal/admin/jobs' : '/portal/jobs'} className="pv2-mono-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 16, color: 'var(--pv2-text-muted)' }}>
        <ArrowLeft size={11} /> Back to jobs
      </Link>

      {loading && <div className="pv2-card" style={{ padding: 40, textAlign: 'center' }}>Loading job…</div>}
      {error && <div className="pv2-alert pv2-alert-red"><AlertCircle size={14} /><span>{error}</span></div>}
      {paymentSuccess && (
        <div className="pv2-alert pv2-alert-green"><CheckCircle size={14} /><span>Payment received — receipt sent to your email.</span></div>
      )}

      {!loading && !error && job && (
        <>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 24, paddingBottom: 24, borderBottom: '2px solid var(--pv2-red)', marginBottom: 32, ...(job.is_rush ? { background: 'rgba(248, 113, 113, 0.06)', borderLeft: '3px solid var(--pv2-red-bright)', paddingLeft: 18, paddingTop: 16, borderTopLeftRadius: 4 } : {}) }}>
            <div>
              <div className="pv2-page-eyebrow" data-testid="pv2-job-priority-label"
                style={{ marginBottom: 12, color: job.is_rush ? 'var(--pv2-red-bright)' : 'var(--pv2-text-muted)', fontWeight: 700 }}>
                {job.is_rush ? 'Rush' : 'Routine'}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 8 }}>
                <h1 className="pv2-page-title" data-testid="pv2-job-num">#{job.servemanager_job_number}</h1>
                <button onClick={copyJobNum} className="pv2-mono"
                  style={{ background: 'transparent', border: '1px solid var(--pv2-ink-line)', padding: '6px 10px', color: 'var(--pv2-text-muted)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                  data-testid="pv2-copy-job-num">
                  <Copy size={12} strokeWidth={1.5} /> {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
              <p style={{ color: 'var(--pv2-text-muted)', fontSize: 14 }}>{job.recipient_name || 'Recipient unknown'}</p>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {(job.service_status === 'Served') ? (
                  <span className="pv2-served-glow"><StatusPill status={job.service_status || job.job_status} /></span>
                ) : (
                  <StatusPill status={job.service_status || job.job_status} />
                )}
              </div>
              {isAdmin && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }} data-testid="pv2-rush-override">
                  <span style={{ fontSize: 11, color: 'var(--pv2-text-subtle)', marginRight: 2 }}>Rush:</span>
                  {[['Rush', true], ['Not Rush', false], ['Auto', null]].map(([label, val]) => {
                    const active = (job.rush_override === val) || (val === null && (job.rush_override === null || job.rush_override === undefined));
                    return (
                      <button key={label} disabled={rushBusy} onClick={() => setRushOverride(val)}
                        data-testid={`pv2-rush-set-${String(label).replace(/\s/g, '-').toLowerCase()}`}
                        style={{
                          fontSize: 11, padding: '3px 9px', cursor: 'pointer',
                          border: '1px solid ' + (active ? 'var(--pv2-red-bright)' : 'var(--pv2-ink-line)'),
                          background: active ? 'var(--pv2-red-bright)' : 'transparent',
                          color: active ? '#fff' : 'var(--pv2-text-muted)',
                        }}>
                        {label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Subtle progress stepper */}
          <JobStatusStepper job={job} />

          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)', gap: 24 }}>
            {/* Main column */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 24, minWidth: 0 }}>
              {/* CASE INFORMATION (above service address) */}
              <section className="pv2-card">
                <div className="pv2-card-head">
                  <h2 className="pv2-card-title">Case Information</h2>
                  <Scale size={16} strokeWidth={1.5} color="var(--pv2-red-bright)" />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 20 }}>
                  {job.case_title && (
                    <div style={{ gridColumn: '1 / -1' }}>
                      <div className="pv2-mono-sm" style={{ marginBottom: 4, color: 'var(--pv2-red-bright)' }}>Case Title</div>
                      <div style={{ fontSize: 14, fontStyle: 'italic' }}>{job.case_title}</div>
                    </div>
                  )}
                  <Field label="Case #" value={job.case_number || job.court_case?.number} mono />
                  <Field label="Court" value={job.court_case?.court_name} />
                  <Field label="Plaintiff" value={job.court_case?.plaintiff} />
                  <Field label="Defendant" value={job.court_case?.defendant} />
                  {job.court_case?.court_county && (
                    <div style={{ gridColumn: '1 / -1' }}>
                      <div className="pv2-mono-sm" style={{ marginBottom: 4, color: 'var(--pv2-red-bright)' }}>County</div>
                      <div style={{ fontSize: 14, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <Building2 size={13} strokeWidth={1.5} color="var(--pv2-text-subtle)" /> {job.court_case.court_county} County
                      </div>
                    </div>
                  )}
                  {!job.case_title && !job.case_number && !job.court_case && (
                    <p style={{ color: 'var(--pv2-text-subtle)', fontSize: 13, fontStyle: 'italic', gridColumn: '1 / -1' }}>No case information available</p>
                  )}
                </div>
              </section>

              {/* SERVICE ADDRESS (full + county) */}
              <section className="pv2-card">
                <div className="pv2-card-head">
                  <h2 className="pv2-card-title">
                    Service Address{(job.all_addresses && job.all_addresses.length > 1) ? 'es' : ''}
                  </h2>
                  <MapPin size={16} strokeWidth={1.5} color="var(--pv2-red-bright)" />
                </div>
                {(job.all_addresses && job.all_addresses.length > 1) ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {job.all_addresses.map((addr, idx) => (
                      <div key={idx} style={{ padding: 14, border: '1px solid var(--pv2-ink-line)', background: addr.primary ? 'rgba(248,113,113,0.04)' : 'transparent' }}>
                        {addr.primary && <div className="pv2-mono-sm" style={{ marginBottom: 6, color: 'var(--pv2-red-bright)' }}>Primary</div>}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
                          <ClickableAddress addr={addr} inline />
                          {addr.county && (
                            <div style={{ fontSize: 12, color: 'var(--pv2-text-subtle)', display: 'inline-flex', alignItems: 'center', gap: 4, marginLeft: 'auto' }}>
                              <Building2 size={11} strokeWidth={1.5} /> {addr.county} County
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
                    {job.address && <ClickableAddress addr={job.address} inline />}
                    {job.address?.county && (
                      <div style={{ fontSize: 12, color: 'var(--pv2-text-subtle)', display: 'inline-flex', alignItems: 'center', gap: 4, marginLeft: 'auto' }}>
                        <Building2 size={11} strokeWidth={1.5} /> {job.address.county} County
                      </div>
                    )}
                    {!job.address && <p style={{ color: 'var(--pv2-text-subtle)', fontSize: 13, fontStyle: 'italic' }}>No address information</p>}
                  </div>
                )}
              </section>

              {/* SERVICE DOCS AND AFFIDAVITS — merged from job.documents (initial service docs)
                  and job.misc_attachments (post-service affidavits/proofs).
                  Always rendered, with a placeholder when nothing has been uploaded yet. */}
              {(() => {
                const isAffidavitTitle = (s) => {
                  const t = (s || '').toLowerCase();
                  return /affidavit|proof of service|return of service/.test(t);
                };
                const isImage = (a) => {
                  const name = (a.file_name || a.title || '').toLowerCase();
                  const ct = (a.content_type || a.mime_type || '').toLowerCase();
                  return ct.startsWith('image/') || /\.(jpe?g|png|gif|webp|heic|heif|bmp|tiff?)$/i.test(name);
                };
                const docs = (job.documents || []).map((d) => ({ ...d, _kind: 'document' }));
                // Exclude images — those belong on the attempt's photo strip, not in
                // the document list. (User reported June 2026: photo uploads were
                // leaking into the merged docs section.)
                const atts = (job.misc_attachments || [])
                  .filter((a) => !isImage(a))
                  .map((a) => ({
                    ...a,
                    _kind: 'attachment',
                    _is_affidavit: a.is_affidavit || isAffidavitTitle(a.title) || isAffidavitTitle(a.file_name),
                  }));
                const items = [...docs, ...atts];
                return (
                  <section className="pv2-card" data-testid="pv2-service-docs-affidavits">
                    <div className="pv2-card-head">
                      <h2 className="pv2-card-title">Service Docs and Affidavits</h2>
                      <ScrollText size={16} strokeWidth={1.5} color="var(--pv2-red-bright)" data-testid="pv2-svc-docs-icon" />
                    </div>
                    {items.length === 0 ? (
                      <div
                        className="pv2-mono-sm"
                        style={{
                          padding: 14,
                          border: '1px dashed var(--pv2-ink-line)',
                          color: 'var(--pv2-text-subtle)',
                          textAlign: 'center',
                          letterSpacing: '0.05em',
                        }}
                        data-testid="pv2-service-docs-empty"
                      >
                        Pending — affidavits and service documents will appear here once filed.
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {items.map((d) => (
                          <button
                            key={`${d._kind}-${d.id}`}
                            onClick={() => downloadDoc(d, d._kind)}
                            style={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              justifyContent: 'space-between',
                              gap: 12,
                              padding: 14,
                              border: '1px solid var(--pv2-ink-line)',
                              background: 'transparent',
                              color: 'var(--pv2-text)',
                              cursor: 'pointer',
                              fontSize: 13,
                              textAlign: 'left',
                            }}
                          >
                            <span style={{ display: 'flex', alignItems: 'flex-start', gap: 12, minWidth: 0, flex: 1 }}>
                              <FileText size={16} strokeWidth={1.5} color="var(--pv2-text-subtle)" style={{ flexShrink: 0, marginTop: 2 }} />
                              <span style={{ minWidth: 0, wordBreak: 'break-word', lineHeight: 1.5 }}>
                                <span style={{ fontWeight: 500 }}>{d.title || d.file_name || `Document ${d.id}`}</span>
                                {d._is_affidavit && (
                                  <span
                                    className="pv2-mono-sm"
                                    style={{ marginLeft: 8, color: 'var(--pv2-green-text)' }}
                                    data-testid="pv2-affidavit-badge"
                                  >
                                    AFFIDAVIT
                                  </span>
                                )}
                              </span>
                            </span>
                            <Download size={16} strokeWidth={1.5} color="var(--pv2-red-bright)" style={{ flexShrink: 0, marginTop: 2 }} />
                          </button>
                        ))}
                      </div>
                    )}
                  </section>
                );
              })()}

              {/* INVOICE SECTION */}
              {!isAdmin && (
                <section className="pv2-card" style={{ borderColor: aging ? aging.color : (isPaid ? 'rgba(74,222,128,0.3)' : 'var(--pv2-ink-line)') }}>
                  <div className="pv2-card-head">
                    <h2 className="pv2-card-title">Invoice</h2>
                    <Receipt size={16} strokeWidth={1.5} color="var(--pv2-red-bright)" />
                  </div>
                  {invoiceLoading ? <div style={{ padding: 24, textAlign: 'center', color: 'var(--pv2-text-muted)' }}>Loading invoice…</div>
                    : !invoice ? (
                      <div style={{ padding: 24, textAlign: 'center' }}>
                        <Receipt size={24} style={{ opacity: 0.3, marginBottom: 8 }} />
                        <div style={{ fontSize: 13, color: 'var(--pv2-text-muted)' }}>No invoice issued yet</div>
                        <div style={{ fontSize: 11, color: 'var(--pv2-text-subtle)', marginTop: 4 }}>An invoice will appear here once generated</div>
                      </div>
                    ) : (
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
                          {isPaid ? <span className="pv2-pill" style={{ color: 'var(--pv2-green-text)' }}>Paid</span>
                            : <span className="pv2-pill" style={{ color: aging.color }}>{aging.label}</span>}
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            <button
                              onClick={viewInvoicePdf}
                              className="pv2-btn pv2-btn-ghost"
                              style={{ padding: '8px 14px', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                              data-testid="pv2-job-view-invoice"
                              title={isPaid ? 'View paid invoice / receipt' : 'View invoice PDF'}
                            >
                              <ExternalLink size={13} /> {isPaid ? 'View Receipt' : 'View Invoice'}
                            </button>
                            {!isPaid && (
                              <button onClick={() => setShowPay(true)} className="pv2-btn pv2-btn-primary" style={{ padding: '8px 14px' }} data-testid="pv2-job-pay-invoice">
                                <CreditCard size={13} /> Pay {fmtUSD(balance)}
                              </button>
                            )}
                          </div>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 16 }}>
                          <div style={{ padding: 14, background: 'var(--pv2-ink-soft)', textAlign: 'center' }}>
                            <div className="pv2-mono-sm">Total</div>
                            <div style={{ fontSize: 18, fontFamily: 'Playfair Display, serif', fontWeight: 700, marginTop: 4 }}>{fmtUSD(invoice.total)}</div>
                          </div>
                          <div style={{ padding: 14, background: 'rgba(74,222,128,0.05)', textAlign: 'center' }}>
                            <div className="pv2-mono-sm" style={{ color: 'var(--pv2-green-text)' }}>Paid</div>
                            <div style={{ fontSize: 18, fontFamily: 'Playfair Display, serif', fontWeight: 700, color: 'var(--pv2-green-text)', marginTop: 4 }}>{fmtUSD(paidDisplay)}</div>
                          </div>
                          <div style={{ padding: 14, background: aging ? `${aging.color}11` : 'rgba(74,222,128,0.05)', textAlign: 'center' }}>
                            <div className="pv2-mono-sm" style={{ color: aging ? aging.color : 'var(--pv2-green-text)' }}>Balance</div>
                            <div style={{ fontSize: 18, fontFamily: 'Playfair Display, serif', fontWeight: 700, color: aging ? aging.color : 'var(--pv2-green-text)', marginTop: 4 }}>{fmtUSD(balance)}</div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--pv2-text-muted)', paddingBottom: 12, borderBottom: '1px solid var(--pv2-ink-line-soft)', marginBottom: 12 }}>
                          <span>Issued: {fmtDate(invoice.issued_on)}</span>
                          <span>Terms: NET30</span>
                        </div>
                        {invoice.line_items?.length > 0 && (
                          <div style={{ marginBottom: 12 }}>
                            <div className="pv2-mono-sm" style={{ marginBottom: 8 }}>Line items</div>
                            {invoice.line_items.slice(0, 3).map((li, i) => (
                              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6 }}>
                                <div>
                                  <div style={{ fontWeight: 500 }}>{li.name}</div>
                                  {li.description && <div style={{ fontSize: 11, color: 'var(--pv2-text-subtle)' }}>{li.description}</div>}
                                </div>
                                <div style={{ fontWeight: 500 }}>{fmtUSD(li.total || li.amount || 0)}</div>
                              </div>
                            ))}
                            {invoice.line_items.length > 3 && <div style={{ fontSize: 11, color: 'var(--pv2-text-subtle)' }}>+{invoice.line_items.length - 3} more items</div>}
                          </div>
                        )}
                        {invoice.payments?.length > 0 && (
                          <div>
                            <div className="pv2-mono-sm" style={{ marginBottom: 8 }}>Payments</div>
                            {invoice.payments.map((p, i) => (
                              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                                <span style={{ color: 'var(--pv2-text-muted)' }}>{fmtDate(p.applied_on)}</span>
                                <span style={{ color: 'var(--pv2-green-text)', fontWeight: 500 }}>+{fmtUSD(p.amount)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                </section>
              )}

              {/* TIMELINE (formerly Attempts) */}
              <section className="pv2-card">
                <div className="pv2-card-head">
                  <h2 className="pv2-card-title">Timeline</h2>
                  <Calendar size={16} strokeWidth={1.5} color="var(--pv2-red-bright)" />
                </div>
                <div className="pv2-timeline">
                  {/* Documents Received */}
                  <div className="pv2-timeline-item">
                    <div className="pv2-timeline-dot" style={{ background: '#60a5fa' }} />
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 500, color: '#60a5fa' }}>Documents Received</div>
                      <div className="pv2-mono-sm" style={{ marginTop: 4 }}>{fmtDateTime(job.received_at || job.created_at)}</div>
                    </div>
                  </div>

                  {/* Each attempt */}
                  {job.attempts?.length > 0 && job.attempts.map((att, i) => {
                    const statusLabel = att.serve_type || att.service_status || (att.success ? 'Served' : `Attempt ${i + 1}`);
                    const lower = String(statusLabel).toLowerCase();
                    // V1 parity: success → green, bad address/non-service → red, otherwise attempt → amber
                    let dotColor;
                    if (att.success) dotColor = 'var(--pv2-green-text)';
                    else if (/bad|non.?service|fail/.test(lower)) dotColor = 'var(--pv2-red-bright)';
                    else dotColor = 'var(--pv2-amber-text)';
                    const photoData = attemptPhotos.find(ap => ap.attempt_id === att.id);
                    const photos = photoData?.photos || [];
                    return (
                      <div key={att.id || i} className="pv2-timeline-item">
                        <div className="pv2-timeline-dot" style={{ background: dotColor }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          {/* Row 1: Left = "Successfully Served"/"Attempt N", Right = service-type pill */}
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 14, fontWeight: 500, color: dotColor }}>{att.success ? 'Successfully Served' : 'Attempt'}</span>
                            <span className="pv2-pill" style={{ color: dotColor, marginLeft: 'auto' }}>{statusLabel}</span>
                          </div>
                          {/* Date / time / address all on one line */}
                          <div className="pv2-mono-sm" style={{ marginTop: 6, color: 'var(--pv2-text-muted)' }}>
                            {fmtDateTime(att.served_at)}{att.address ? ` · at ${fmtAddress(att.address)}` : ''}
                          </div>
                          {att.description && (
                            <div style={{ fontSize: 13, color: 'var(--pv2-text-muted)', marginTop: 8, padding: 12, background: 'var(--pv2-ink-soft)', borderLeft: '2px solid var(--pv2-ink-line)', fontStyle: 'italic' }}>
                              &ldquo;{att.description}&rdquo;
                            </div>
                          )}
                          {/* Server name + GPS share a row: server on left, GPS right-aligned */}
                          {(att.server_name || (att.lat && att.lng)) && (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginTop: 6 }}>
                              {att.server_name ? (
                                <span style={{ fontSize: 11, color: 'var(--pv2-text-subtle)' }}>
                                  Server: {att.server_name}
                                </span>
                              ) : <span />}
                              {att.lat && att.lng && (
                                <a href={`https://www.google.com/maps?q=${att.lat},${att.lng}`} target="_blank" rel="noopener noreferrer"
                                  className="pv2-mono-sm"
                                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--pv2-red-bright)', marginLeft: 'auto' }}>
                                  <MapPin size={11} strokeWidth={1.5} /> GPS: {Number(att.lat).toFixed(5)}, {Number(att.lng).toFixed(5)}
                                </a>
                              )}
                            </div>
                          )}
                          {photos.length > 0 && (
                            <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--pv2-ink-line-soft)' }}>
                              <div className="pv2-mono-sm" style={{ marginBottom: 8, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                <Camera size={11} strokeWidth={1.5} /> {photos.length} Photo{photos.length > 1 ? 's' : ''}
                              </div>
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))', gap: 8 }}>
                                {photos.map((photo, pIdx) => (
                                  <button
                                    key={photo.id || pIdx}
                                    type="button"
                                    onClick={() => setLightbox({ images: photos.map(p => p.url), index: pIdx, attempt: i + 1 })}
                                    style={{
                                      display: 'block',
                                      height: 80,
                                      padding: 0,
                                      overflow: 'hidden',
                                      border: '1px solid var(--pv2-ink-line)',
                                      background: 'transparent',
                                      cursor: 'pointer',
                                    }}
                                    title="Click to view"
                                    data-testid="pv2-attempt-photo-thumb"
                                  >
                                    <img src={photo.url} alt={`Attempt photo ${pIdx + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            </div>

            {/* Sidebar */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 24, minWidth: 0 }}>
              <section className="pv2-card">
                <div className="pv2-card-head"><h2 className="pv2-card-title">Details</h2></div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <Field label="Status" value={<StatusPill status={job.service_status || job.job_status} />} />
                  <Field label="Received" value={fmtDate(job.received_at || job.created_at)} />
                  <Field label="Last updated" value={fmtDateTime(job.updated_at)} />
                  {lastAttempt?.server_name && (
                    <div>
                      <div className="pv2-mono-sm" style={{ marginBottom: 4 }}>Last Server</div>
                      <div style={{ fontSize: 14 }}>{lastAttempt.server_name}</div>
                    </div>
                  )}
                </div>
              </section>
            </div>
          </div>

          {/* Payment overlay — backdrop click intentionally does NOT close the modal,
              to prevent the in-flight Stripe form from being dismissed mid-payment.
              Closure is via the explicit X button only, or auto-redirect on success. */}
          {showPay && invoice && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)', zIndex: 100, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: 32 }}>
              <div style={{ width: '100%', maxWidth: 720 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, color: '#fff' }}>
                  <div>
                    <div className="pv2-mono-sm">Secure Payment</div>
                    <div style={{ fontSize: 16, fontWeight: 500, marginTop: 4 }}>Pay Invoice — Job #{job.servemanager_job_number}</div>
                    <div style={{ fontSize: 13, color: 'var(--pv2-text-muted)', marginTop: 2 }}>
                      Amount: <span style={{ color: '#fff', fontWeight: 600 }}>{fmtUSD(balance)}</span>
                    </div>
                  </div>
                  <button onClick={closePay} style={{ background: 'transparent', border: '1px solid var(--pv2-ink-line)', color: '#fff', padding: 8, cursor: 'pointer' }}>
                    <X size={16} />
                  </button>
                </div>
                <div data-testid="pv2-job-pay-modal">
                  <EmbeddedStripePaymentElement
                    createIntent={createPaymentIntent}
                    returnUrl={`${window.location.origin}/payment-success?continue=${encodeURIComponent('/portal/jobs/' + jobId)}`}
                    onSuccess={(pi) => {
                      setShowPay(false);
                      navigate(`/payment-success?payment_intent_id=${pi?.id || ''}&continue=${encodeURIComponent('/portal/jobs/' + jobId)}`);
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Lightbox gallery for attempt photos. Opens on thumbnail click.
              When an attempt has multiple photos, left/right arrows (and the
              ← → keyboard keys / swipe) let the viewer scroll between them
              without closing. Backdrop click closes. */}
          {lightbox && (() => {
            const currentUrl = lightbox.images[lightbox.index];
            return (
            <div
              onClick={() => setLightbox(null)}
              style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', backdropFilter: 'blur(8px)', zIndex: 110, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32 }}
              data-testid="pv2-photo-lightbox"
            >
              {/* Prev arrow */}
              {showLightboxNav && (
                <button
                  onClick={(e) => { e.stopPropagation(); stepLightbox(-1); }}
                  aria-label="Previous photo"
                  data-testid="pv2-photo-lightbox-prev"
                  style={{ position: 'absolute', left: 20, top: '50%', transform: 'translateY(-50%)', width: 52, height: 52, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.55)', border: '1px solid var(--pv2-ink-line)', color: '#fff', cursor: 'pointer' }}
                >
                  <ChevronLeft size={26} />
                </button>
              )}

              <div onClick={(e) => e.stopPropagation()} style={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'center' }}>
                <img src={currentUrl} alt={`Attempt ${lightbox.attempt || ''} photo ${lightbox.index + 1}`} style={{ maxWidth: '100%', maxHeight: '78vh', objectFit: 'contain', border: '1px solid var(--pv2-ink-line)', background: '#000' }} />
                {showLightboxNav && (
                  <div className="pv2-mono-sm" data-testid="pv2-photo-lightbox-counter" style={{ letterSpacing: '0.14em' }}>
                    {lightbox.index + 1} / {lightboxCount}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <a
                    href={currentUrl}
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                    className="pv2-btn pv2-btn-primary"
                    style={{ padding: '8px 14px', display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none' }}
                    data-testid="pv2-photo-lightbox-download"
                  >
                    <Download size={13} /> Download
                  </a>
                  <button
                    onClick={() => setLightbox(null)}
                    className="pv2-btn pv2-btn-ghost"
                    style={{ padding: '8px 14px', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                    data-testid="pv2-photo-lightbox-close"
                  >
                    <X size={13} /> Close
                  </button>
                </div>
              </div>

              {/* Next arrow */}
              {showLightboxNav && (
                <button
                  onClick={(e) => { e.stopPropagation(); stepLightbox(1); }}
                  aria-label="Next photo"
                  data-testid="pv2-photo-lightbox-next"
                  style={{ position: 'absolute', right: 20, top: '50%', transform: 'translateY(-50%)', width: 52, height: 52, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.55)', border: '1px solid var(--pv2-ink-line)', color: '#fff', cursor: 'pointer' }}
                >
                  <ChevronRight size={26} />
                </button>
              )}
            </div>
            );
          })()}
        </>
      )}
    </PortalV2Layout>
  );
};

export default JobV2Detail;
